import { ConnectEntity } from 'components/ConnectEntity';
import { QualifiedEntities } from 'components/VatChecker';
import { ViesEntityDetails } from 'components/VatChecker/entity-details';
import { UpgradeMessage } from 'containers/UpgradeMessage';
import { UpgradeMessageSource } from 'store/api/enums';
import { EmptyPage } from 'containers/EmptyPage';
import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import routes from 'routes';
import { turnDrawerOn, turnModalOff, turnModalOn } from 'store/app/actions';
import { DrawerType, LicenseStatus, WindowType } from 'store/app/enums';
import { DrawerData, ModalData } from 'store/app/types';
import { getAuthUser, getAuthUserLicence } from 'store/auth/selectors';
import { getCommonCountries } from 'store/common/selectors';
import {
  disconnectOrganisationsRequest,
  updateOrganisationData,
} from 'store/organisation/actions';
import {
  getOrgLoading,
  getOrgOrganisations,
} from 'store/organisation/selectors';
import {
  activateViesRequest,
  deactivateViesRequest,
  fetchViesInfoRequest,
} from 'store/vat-checker/actions';
import { ViesInfo } from 'store/vat-checker/models';
import {
  getViesInfos,
  getViesLoading,
  getViesSuccess,
} from 'store/vat-checker/selectors';
import { checkPageLoaded } from 'utils/check-page-loaded';
import {
  PreferenceActions,
  Preference,
  usePreferences,
  PreferenceSections,
} from 'utils/preferences';
import { useTranslation } from 'react-i18next';

export const ACTIVATED = 'activated';
export const QUALIFIED = 'qualified';
export const VIES_TUTORIAL_HIDE = 'vies-tutorial-hide';

export const VIESPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const orgs = useSelector(getOrgOrganisations);
  const countries = useSelector(getCommonCountries);
  const viesInfos = useSelector(getViesInfos);
  const viesLoading = useSelector(getViesLoading);
  const orgLoading = useSelector(getOrgLoading);
  const currentUser = useSelector(getAuthUser);
  const userLicence = useSelector(getAuthUserLicence);
  const loading = viesLoading || orgLoading;
  const success = useSelector(getViesSuccess);

  const [showPage, setShowPage] = React.useState(false);
  const [hideAll, setHideAll] = React.useState(true);
  const [activeTabKey, setActiveTabKey] = React.useState(ACTIVATED);
  const [maximumReached, setMaximumReached] = React.useState(false);
  const initialTabSet = React.useRef(false);
  const [initialDataFetched, setInitialDataFetched] = React.useState(false);
  const preferences = usePreferences();

  const handlePreferences = (
    action: PreferenceActions,
    preference: Preference
  ) => {
    if (currentUser)
      return preferences(
        action,
        PreferenceSections.VIES,
        preference,
        currentUser?.userUuid
      );
    return false;
  };

  React.useEffect(() => {
    setMaximumReached(
      viesInfos?.filter((entity) => entity.vatCheckerActive).length >=
        userLicence.maxVatCheckers
    );
  }, [viesInfos, userLicence]);
  // Skeleton should only show while we have fewer than a minimum number of
  // loaded entities. Once we reach that minimum (or all entities if total < min),
  // we show the real page even if background refreshes continue.
  const minimumLoadedEntities = React.useMemo(() => {
    const totalOrgs = orgs?.length ?? 0;
    const desiredMin = 3;
    return totalOrgs > 0 ? Math.min(desiredMin, totalOrgs) : desiredMin;
  }, [orgs]);

  const skeletonLoading =
    (orgLoading && (!orgs || orgs.length === 0)) ||
    (viesLoading && (!viesInfos || viesInfos.length < minimumLoadedEntities));

  // Track when initial data fetch completes
  React.useEffect(() => {
    if (
      viesInfos &&
      viesInfos.length > 0 &&
      !viesLoading &&
      !initialDataFetched
    ) {
      setInitialDataFetched(true);
    }
  }, [viesInfos, viesLoading, initialDataFetched]);

  // Set initial tab based on available entities (only runs once after loading is complete and data has stabilized)
  React.useEffect(() => {
    if (
      viesInfos &&
      viesInfos.length > 0 &&
      orgs &&
      orgs.length > 0 &&
      viesInfos.length === orgs.length && // Ensure all orgs have viesInfo
      initialDataFetched &&
      !viesLoading &&
      !orgLoading &&
      !initialTabSet.current
    ) {
      // Wait a bit to ensure all entity updates have completed
      const timeoutId = setTimeout(() => {
        if (initialTabSet.current) return; // Already set by another update

        const hasActivatedEntities = viesInfos.some(
          (entity) => entity.vatCheckerActive
        );
        const hasQualifiedEntities = viesInfos.some(
          (entity) => !entity.vatCheckerActive
        );

        // If no activated entities but there are qualified ones, show qualified tab
        if (!hasActivatedEntities && hasQualifiedEntities) {
          setActiveTabKey(QUALIFIED);
        }
        initialTabSet.current = true;
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [viesInfos, orgs, initialDataFetched, viesLoading, orgLoading]);

  const activateTab = (key: string) => {
    setActiveTabKey(key);
  };

  React.useEffect(() => {
    if (success) {
      setActiveTabKey(ACTIVATED);
    }
  }, [success]);

  React.useEffect(() => {
    setShowPage(true);
    currentUser &&
      checkPageLoaded(location.pathname, routes.vies.qualifiedEntities);
  }, []);

  React.useEffect(() => {
    if (orgs && orgs.length > 0) {
      // Only fetch if there are changes in friendly names
      const hasNameChanges = orgs.some((org) => {
        const matchingViesInfo = viesInfos.find(
          (info) => info.connectionId === org.connectionId
        );
        return (
          matchingViesInfo &&
          matchingViesInfo.organisationName !== org.friendlyName
        );
      });

      if (hasNameChanges || !viesInfos.length) {
        dispatch(fetchViesInfoRequest(undefined, true));
      }
    }
    if (orgs && !loading) setHideAll(false);
    else {
      setHideAll(true);
    }
  }, [orgs, loading, viesInfos]);

  const activateVatChecker = (connectionId: string, entityName: string) => {
    const activate = () => {
      dispatch(activateViesRequest(connectionId, entityName));
      dispatch(turnModalOff());
    };
    const goToBilling = () => {
      navigate(routes.billing.root);
      dispatch(turnModalOff());
    };
    if (userLicence?.status === LicenseStatus.UNLIMITED) {
      activate();
    } else {
      if (maximumReached) {
        const modalData: ModalData = {
          title: '',
          type: WindowType.WARNING,
          component: (
            <UpgradeMessage
              source={UpgradeMessageSource.VAT_CHECKER_ACTIVATE}
              callback={activate}
              onClose={() => dispatch(turnModalOff())}
              goToBilling={goToBilling}
            />
          ),
          maskClosable: false,
        };
        dispatch(turnModalOn(modalData));
      } else {
        activate();
      }
    }
  };

  const deactivateVatChecker = (
    connectionId: string,
    entityName: string,
    entityId: string
  ) => {
    dispatch(deactivateViesRequest(connectionId, entityName));
    dispatch(updateOrganisationData(entityId, false));
  };

  const loadCustomersAndSuppliers = (connectionId: string) => {
    navigate(
      routes.vies.customersAndSuppliers.replace(':connectionId', connectionId)
    );
  };

  const disconnectOrganisation = (organisationId: string) => {
    const fromVIES = true;
    dispatch(disconnectOrganisationsRequest(organisationId, fromVIES));
  };

  const onEditEntity = (viesInfo: ViesInfo) => {
    const organisation = orgs?.find(
      (o) => o.connectionId === viesInfo.connectionId
    );
    const drawerData: DrawerData = {
      title: t('organisation.organisation-details'),
      //subtitle: 'Entity Name',
      type: DrawerType.CONNECT_ENTITY,
      component: (
        <ViesEntityDetails
          organisation={organisation}
          countries={countries}
          activeEntity={viesInfo.vatCheckerActive}
          activateVatChecker={activateVatChecker}
          deactivateVatChecker={deactivateVatChecker}
          disconnectOrganisation={disconnectOrganisation}
          currentUser={currentUser}
        />
      ),
    };
    dispatch(turnDrawerOn(drawerData));
  };

  const emptyPageCallback = () => {
    const drawerData: DrawerData = {
      title: t('organisation.add-new-entity'),
      subtitle: t('organisation.connect-entity-subtitle'),
      type: DrawerType.CONNECT_ENTITY,
      component: <ConnectEntity />,
    };
    dispatch(turnDrawerOn(drawerData));
  };

  const [hasScrollbar, setHasScrollbar] = React.useState(false);

  React.useEffect(() => {
    const checkScrollbar = () => {
      // Use setTimeout to ensure DOM is fully rendered
      setTimeout(() => {
        const hasVerticalScrollbar =
          document.documentElement.scrollHeight >
          document.documentElement.clientHeight;
        const scrollbarWidth =
          window.innerWidth - document.documentElement.clientWidth;
        setHasScrollbar(hasVerticalScrollbar && scrollbarWidth > 0);
      }, 100);
    };

    checkScrollbar();
    window.addEventListener('resize', checkScrollbar);
    window.addEventListener('scroll', checkScrollbar);
    const resizeObserver = new ResizeObserver(checkScrollbar);
    if (document.body) {
      resizeObserver.observe(document.body);
    }

    // Also check after a delay to catch late-rendered content
    const timeoutId = setTimeout(checkScrollbar, 500);

    return () => {
      window.removeEventListener('resize', checkScrollbar);
      window.removeEventListener('scroll', checkScrollbar);
      resizeObserver.disconnect();
      clearTimeout(timeoutId);
    };
  }, [orgs, loading, viesInfos]);

  return (
    <React.Fragment>
      {showPage && (
        <>
          <Helmet>
            <title>{`${t('vat-checker.vies-page-title')} | ${process.env.PROJECT_NAME}`}</title>
            <meta
              name="description"
              content={t('vat-checker.page-meta-description')}
            />
          </Helmet>
          <div
            style={{
              padding: '40px',
              paddingTop: '10px',
              maxWidth: hasScrollbar ? 1662 : 1678,
              minWidth: 1200,
              margin: 'auto',
            }}
          >
            {orgs && orgs.length > 0 ? (
              <QualifiedEntities
                viesInfos={viesInfos}
                loadCustomersAndSuppliers={loadCustomersAndSuppliers}
                activateVatChecker={activateVatChecker}
                deactivateVatChecker={deactivateVatChecker}
                loading={skeletonLoading}
                onEditEntity={onEditEntity}
                hideAll={hideAll}
                currentUser={currentUser}
                activateTab={activateTab}
                activeTabKey={activeTabKey}
                handlePreferences={handlePreferences}
                noEntities={!orgs || orgs.length === 0}
                organisations={orgs}
              />
            ) : (
              <EmptyPage
                titleText={t('organisation.empty-entities-title')}
                actionText={t('organisation.empty-entities-action')}
                buttonText={t('organisation.empty-entities-button')}
                actionCallback={emptyPageCallback}
              />
            )}
          </div>
        </>
      )}
    </React.Fragment>
  );
};
